import React from 'react';
// Pilot disabled: restore when account settings returns.
// import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const ProfileCard = ({ user, metrics, weeklyActivity = [], achievements = [] }) => {
  // Pilot disabled: restore with the Manage Profile button.
  // const navigate = useNavigate();

  // Resiliently resolve user details
  const name = user?.full_name?.Valid 
    ? user.full_name.String 
    : (typeof user?.full_name === 'string' ? user.full_name : (user?.username || 'User'));
    
  const handle = user?.username ? `@${user.username}` : '';
  const college = user?.college || '';
  const targetRole = user?.target_role || '';
  const passingYear = user?.graduation_year || '';
  const hasResume = !!user?.resume_url;
  
  const rank = metrics?.global_rank ? `#${metrics.global_rank.toLocaleString()}` : 'N/A';
  const points = metrics?.total_points ? metrics.total_points.toLocaleString() : '0';
  const currentStreak = metrics?.current_streak || 0;
  const longestStreak = metrics?.longest_streak || 0;
  const avgScore = metrics?.avg_score ? `${Math.round(metrics.avg_score)}%` : '0%';

  const profilePic = user?.profile_picture_url?.Valid 
    ? user.profile_picture_url.String 
    : (typeof user?.profile_picture_url === 'string' ? user.profile_picture_url : null);

  const getInitials = () => name.slice(0, 2).toUpperCase();
  
  const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col overflow-hidden">
      {/* Top Banner & Avatar Area */}
      <div className="relative h-20 bg-gradient-to-r from-slate-900 to-slate-800">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '12px 12px' }} />

        <div className="absolute -bottom-10 left-4">
          <div className="w-20 h-20 bg-white rounded-2xl p-1 shadow-md">
            <div className="w-full h-full bg-slate-100 rounded-[14px] overflow-hidden flex items-center justify-center border border-slate-100">
              {profilePic ? (
                <img src={profilePic} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <span className="text-2xl font-normal text-slate-400">{getInitials()}</span>
              )}
            </div>
          </div>
          <div className="absolute bottom-1 right-1 w-5 h-5 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center shadow-sm">
            <Icon name="Check" size={10} color="#FFFFFF" />
          </div>
        </div>
      </div>

      <div className="pt-12 px-4 pb-4">
        {/* Basic Info */}
        <div className="mb-4">
          <div className="flex items-center gap-1.5 mb-1.5">
            <h2 className="text-[18px] font-normal text-slate-900 truncate leading-tight">{name}</h2>
            <div className="flex items-center justify-center w-4 h-4 rounded-full bg-blue-500 shrink-0">
               <Icon name="Check" size={10} color="#FFFFFF" className="fill-white" />
            </div>
          </div>
          <p className="text-[13px] text-slate-400 truncate mb-4">{handle}</p>

          {/* Achievements - Premium Look in White Space */}
          {Array.isArray(achievements) && achievements.some(a => a.isUnlocked) && (
            <div className="flex flex-wrap gap-2.5 mb-2">
              {achievements.filter(a => a.isUnlocked).slice(0, 4).map((item, idx) => (
                <div 
                    key={idx} 
                    className="relative group cursor-help"
                >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-50 to-orange-100 border-[1.5px] border-amber-200 flex items-center justify-center transition-all hover:scale-110 shadow-sm overflow-hidden">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_transparent_40%,_rgba(245,158,11,0.08)_100%)]" />
                        <Icon name={item.icon || "Trophy"} size={16} className="text-amber-500 relative z-10" />
                    </div>

                    {/* Premium Hover Tooltip */}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 pointer-events-none opacity-0 group-hover:opacity-100 transition-all duration-200 z-[100]">
                        <div className="bg-slate-900 text-white px-3 py-2 rounded-xl shadow-xl shadow-slate-200/50 whitespace-nowrap flex flex-col items-center min-w-[120px]">
                            <span className="text-[10px] font-normal tracking-tight flex items-center gap-1.5 mb-1 text-amber-400">
                                <Icon name="Trophy" size={10} className="fill-amber-400" />
                                {item.name || item.title || 'Unlocked'}
                            </span>
                            {item.description && (
                                <span className="text-[9px] text-slate-300 font-normal max-w-[150px] text-center leading-relaxed">
                                    {item.description}
                                </span>
                            )}
                            {/* Micro-arrow */}
                            <div className="absolute top-[95%] left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-900 rotate-45" />
                        </div>
                    </div>
                </div>
              ))}
              {achievements.filter(a => a.isUnlocked).length > 4 && (
                <div className="w-10 h-10 rounded-full bg-slate-50 border-[1.5px] border-slate-100 flex items-center justify-center text-slate-400 text-[10px] font-normal shadow-sm">
                    +{achievements.filter(a => a.isUnlocked).length - 4}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Academic/Professional Details */}
        <div className="space-y-2.5 mb-5 p-3 bg-slate-50/50 rounded-xl border border-slate-100">
          <div className="flex items-center gap-2.5 text-slate-600">
            <div className="w-6 h-6 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0">
              <Icon name="Briefcase" size={12} className="text-slate-400" />
            </div>
            <span className="text-[12px] font-normal text-slate-700 truncate">{targetRole || 'Target Role Not Set'}</span>
          </div>
          <div className="flex items-center gap-2.5 text-slate-600">
            <div className="w-6 h-6 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0">
              <Icon name="GraduationCap" size={12} className="text-slate-400" />
            </div>
            <span className="text-[12px] font-normal text-slate-700 truncate">
              {college || 'Institute Not Added'} {passingYear ? `(${passingYear})` : ''}
            </span>
          </div>
          <div className="flex items-center gap-2.5 text-slate-600">
            <div className="w-6 h-6 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0">
              <Icon name="FileText" size={12} className={hasResume ? 'text-emerald-500' : 'text-slate-400'} />
            </div>
            <span className={`text-[12px] font-normal ${hasResume ? 'text-emerald-600' : 'text-slate-500'} truncate`}>
              {hasResume ? 'Resume Verified' : 'No Resume Uploaded'}
            </span>
          </div>
        </div>

        {/* Weekly Streak Visualization */}
        <div className="mb-1 px-1 pt-2">
          <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-normal text-slate-900 tracking-wider">Weekly Consistency</span>
              <div className="flex items-center gap-1 bg-orange-50 px-2 py-0.5 rounded-full border border-orange-100">
                  <Icon name="Flame" size={10} className="text-orange-500 fill-orange-500" />
                  <span className="text-[11px] font-normal text-orange-700">{currentStreak} Day Streak</span>
              </div>
          </div>
          <div className="grid grid-cols-7 gap-2">
              {days.map((day, i) => {
                  const isActive = weeklyActivity[i];
                  return (
                      <div key={i} className="flex flex-col items-center gap-1.5 group/day relative">
                          <span className="text-[9px] font-normal text-slate-300 group-hover/day:text-slate-500 transition-colors">{day}</span>
                          <div className={`w-full aspect-square rounded-md border-2 transition-all cursor-default ${
                              isActive 
                              ? 'bg-emerald-500 border-emerald-600 shadow-md shadow-emerald-100' 
                              : 'bg-slate-50 border-slate-100'
                          }`} />
                          
                          {/* Mini Tooltip */}
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 pointer-events-none opacity-0 group-hover/day:opacity-100 transition-all duration-200 z-[100]">
                              <div className="bg-slate-900 text-white px-2 py-1 rounded-md shadow-lg whitespace-nowrap">
                                  <p className="text-[8px] font-normal tracking-widest leading-none">
                                      {isActive ? '✅ Active' : '⭕ No Activity'}
                                  </p>
                                  <div className="absolute top-[90%] left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-slate-900 rotate-45" />
                              </div>
                          </div>
                      </div>
                  );
              })}
          </div>
      </div>

      {/* Pilot disabled: Manage Profile opens account settings.
      <button 
        onClick={() => navigate('/settings')}
        className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-[12px] font-normal tracking-wide transition-all shadow-lg shadow-slate-200"
      >
        Manage Profile
      </button>
      */}
      </div>
    </div>
  );
};

export default ProfileCard;
